import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { Pizza, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) {
      navigate(user.role === "admin" ? "/admin/entries" : "/manager", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(username.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      navigate(res.user.role === "admin" ? "/admin/entries" : "/manager", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left: cinematic image */}
      <div
        className="relative hidden md:block"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/3343622/pexels-photo-3343622.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=950&w=940')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="login-image"
      >
        <div className="absolute inset-0 bg-zinc-950/55" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#DF1A22] flex items-center justify-center">
              <Pizza className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display font-bold tracking-tight">LABOUR TRACKER</div>
          </div>
          <div>
            <div className="label-uc text-zinc-300">Operational Console</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-[1.05]">
              Track every hour.<br />Log every delivery.
            </h1>
            <p className="text-zinc-300 mt-4 max-w-md text-sm leading-relaxed">
              Daily labour, hours, and delivery data — captured by store managers, surfaced as one source of truth.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="label-uc">Sign in</div>
          <h2 className="font-display text-3xl font-bold tracking-tight mt-2">Welcome back.</h2>
          <p className="text-sm text-zinc-500 mt-1">Use your manager or admin credentials.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="username" className="label-uc">Username</Label>
              <Input
                id="username"
                type="text"
                required
                autoComplete="username"
                autoCapitalize="none"
                spellCheck="false"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="rounded-none border-zinc-300 mt-1.5 focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-0"
                data-testid="login-username-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-uc">Password</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none border-zinc-300 mt-1.5 focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-0"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-sm bg-[#FEE2E2] text-[#991B1B] px-3 py-2 border border-red-200" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-none bg-zinc-950 hover:bg-zinc-800 text-white h-11"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-zinc-200 text-xs text-zinc-500">
            Need an account? Ask your administrator to set you up.
          </div>
        </div>
      </div>
    </div>
  );
}
